import {catchError, finalize, map, mergeMap, shareReplay, takeUntil} from 'rxjs/operators';
import {forkJoin, Observable, of, throwError, Subject} from 'rxjs';
import {Component, HostListener, Inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DOCUMENT} from '@angular/common';
import * as dayjs from 'dayjs';

import minBy from '../../../../utils/minBy';

import {ICruiseDetailSolution} from './interfaces/icruise-detail-solution';
import {ICruiseDetailParams} from './interfaces/icruise-detail-params';
import {ICruiseDetailCabin} from './interfaces/icruise-detail-cabin';
import {IShipServiceRemote} from './interfaces/iship-service-remote';
import {LoadingItem} from '../../../shared/interfaces/loading-item';
import {Occupancy} from '../../../shared/interfaces/occupancy';
import {ICruiseDetail} from './interfaces/icruise-detail';

import {NetworkConfigs, Networks} from '../../../shared/models/networks.enum';
import {CruiseTopId} from '../../../shared/models/cruise-top-id.enum';
import {CruiseDetailTabs} from './models/cruise-detail-tabs.enum';

import {CruiseBookingService} from '../cruise-booking-page/services/cruise-booking.service';
import {PostMessageService} from '../../../shared/services/post-message.service';
import {NavigationService} from '../../../../services/navigation.service';
import {LoadingService} from '../../../shared/services/loading.service';
import {ProjectService} from '../../../shared/services/project.service';
import {BookingService} from '../../../shared/services/booking.service';
import {WidgetService} from '../../../shared/services/widget.service';
import {CruiseDetailService} from './service/cruise-detail.service';
import {CmsService} from 'src/app/modules/cms/services/cms.service';
import {SEOService} from '../../../shared/services/seo.service';
import {BookingObjectService} from '../../../../services/booking-object.service';
import { ICruiseDetailStep } from './interfaces/icruise-detail-step';

@Component({
  selector: 'app-cruise-detail',
  templateUrl: './cruise-detail.component.html',
  styleUrls: ['./cruise-detail.component.scss']
})
export class CruiseDetailComponent implements OnInit, OnDestroy {

  cruise$: Observable<ICruiseDetail>;
  shipService$: Observable<IShipServiceRemote>;
  selectedTab: CruiseDetailTabs;
  cruiseDetailTabs = CruiseDetailTabs;
  bookingUrl: string;
  noDetail: boolean;

  fixedRecap: boolean = false;

  routeParams: any;

  isLanding: boolean;

  brand: any;
  isWidget$: Observable<boolean>;
  selectedSolutionOnPriceGrid: ICruiseDetailSolution;
  selectedCabinOnPriceGrid: ICruiseDetailCabin;

  loadingPriceGridStatus: boolean = false;

  private occupancy: Occupancy;
  private unsubscribe$ = new Subject();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private _router: Router,
    private _cruiseDetailService: CruiseDetailService,
    private _cruiseBookingService: CruiseBookingService,
    private _seoService: SEOService,
    private _projectService: ProjectService,
    private _route: ActivatedRoute,
    private _cmsService: CmsService,
    private _loadingService: LoadingService,
    private _navigationService: NavigationService,
    private _postMessageService: PostMessageService,
    private _widgetService: WidgetService,
    private _bookingService: BookingService,
    private _bookingObject: BookingObjectService
  ) { }

  //  Gestione del recap fisso sul bottom della view da mobile
  @HostListener('window:scroll', ['$event'])
  onWindowScroll($event: any) {
    this.fixedRecap = window.scrollY >= 100 ? true : false;
    const recapContainer = (document.querySelector("div.detail-recap__container") as HTMLElement);
    if (!recapContainer || this.isLanding) return;
    const offset = recapContainer?.offsetHeight;
    if (window.innerWidth <= 992 && this.fixedRecap && document.querySelector('footer')) {
      document.querySelector('footer').style.marginBottom = offset + 'px';
    } else if (document.querySelector('footer')) {
      document.querySelector('footer').style.marginBottom = '0';
    }
  }

  ngOnInit(): void {
    this.goToTop();
    this.isWidget$ = this._widgetService.isWidget();
    const segments = this._route.snapshot['_urlSegment'].segments.map(x => x.path);
    this.isLanding = segments[0] == 'landing';

    if (this.isLanding) {
      const queryParams = this._route.snapshot.queryParams;
      const networkConfig = NetworkConfigs[Number(queryParams.network)];
      this._projectService.network = networkConfig.network;
      this.brand = networkConfig;
      let bodyEl = this.document.getElementById("body-el");
      bodyEl.classList.add(networkConfig.bodyClass);

      this.initCruiseDetailInfo(segments);
    } else {
      this._cmsService.brandConfig$.pipe(takeUntil(this.unsubscribe$)).subscribe(brand => {
        if (brand) {
          this.brand = brand;
        }
        this.initCruiseDetailInfo();
      });
    }

  }

  ngOnDestroy(): void {
    this._cruiseBookingService.selectedSolution$.next(undefined);
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();

    // reimposto il margin bottom del footer a 0
    if (document.querySelector('footer')) {
      document.querySelector('footer').style.marginBottom = '0';
    }
  }

  goToTop() {
    this._postMessageService.scrollToElementPos(0)
  }

  initCruiseDetailInfo(segments?): void {
    this.occupancy = this._bookingService.occupancy; // "occupancy" di default

    this._route.paramMap.subscribe(e => {
      this.routeParams = {
        source : this.isLanding ? segments[3] : e.get('source'),
        hasFlight : this.isLanding ? segments[4] : e.get('hasFlight'),
        packetString : this.isLanding ? segments[6] : e.get('packetDescription'),
        flightString : this.isLanding ? segments[8] : e.get('flightString'),
        fromDate : this.isLanding ? segments[10] : e.get('fromDate'),
        toDate : this.isLanding ? segments[12] : e.get('toDate')
      }

      this.bookingUrl = `/crociere/booking/${this.routeParams.source}/${this.routeParams.hasFlight}`;
      this.bookingUrl += `/pacchetto/${this.routeParams.packetString}/volo/${this.routeParams.flightString}`;
      this.bookingUrl += `/dal/${this.routeParams.fromDate}/al/${this.routeParams.toDate}`;

      this._navigationService.getRedirectUrl(this.bookingUrl).then(
          (resolved) => {
            this.bookingUrl = resolved.url;
          }
      );
    });

    // FIXME: da rifattorizzare con un unico BehaviorSubject con 2 parametri di ingresso per solution e cabin
    // verifico se l'impostazione per la visualizzazione della griglia solutions/prezzi è true in modo da effettuare
    // alcuni subscribe per poter aggiornare l'eventuale solution e cabina (e successivamente anche il link del booking)
    if (this.brand.detailPageConfig.showPriceGrid) {
      this._cruiseBookingService.selectedSolution$
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((solution) => {
          if (solution) {
            this.selectedSolutionOnPriceGrid = solution;

            // se è stata selezionata una solution, allora eseguo anche un subscribe della selectedCabin in modo da
            // poter aggiornare il link del booking
            this._cruiseBookingService.selectedCabin$
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe((cabin) => {
                if (cabin) {
                  this.selectedCabinOnPriceGrid = cabin;

                  // recupero gli identificativi della solution e della cabina selezionata
                  const solutionId = this.selectedSolutionOnPriceGrid.id || 'null';
                  const cabinId = this.selectedCabinOnPriceGrid.id || 'null';
                  const cabinCode = this.selectedCabinOnPriceGrid.code || 'null';
                  const cabinFare = this.selectedCabinOnPriceGrid.fare || 'null';

                  // formatto le date di partenza e arrivo
                  const fromDateUpdated = this.selectedSolutionOnPriceGrid.departureDate ?
                      dayjs(this.selectedSolutionOnPriceGrid.departureDate).format('DD-MM-YYYY') : this.routeParams.fromDate;
                  const toDateUpdated = this.selectedSolutionOnPriceGrid.departureDate ?
                      dayjs(this.selectedSolutionOnPriceGrid.arrivalDate).format('DD-MM-YYYY') : this.routeParams.toDate;

                  // aggiorno il link del booking
                  this.bookingUrl = `/crociere/booking/${this.routeParams.source}/${this.routeParams.hasFlight}`;
                  this.bookingUrl += `/pacchetto/${this.routeParams.packetString}/volo/${this.routeParams.flightString}`;
                  this.bookingUrl += `/dal/${fromDateUpdated}/al/${toDateUpdated}`;
                  this.bookingUrl += `/${solutionId}/${cabinId}/${cabinCode}/${cabinFare}`;
                  this._navigationService.getRedirectUrl(this.bookingUrl).then(
                      (resolved) => {
                        this.bookingUrl = resolved.url;
                      }
                  );
                }
              });
          }
        });
    }

    this._cruiseBookingService.isLoadingUpdateSolutionFromCruiseCat$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isLoadingUpdateSolutionFromCruiseCat) => {
        this.loadingPriceGridStatus = isLoadingUpdateSolutionFromCruiseCat;
      });

    this._route.paramMap.pipe(
        mergeMap(e => forkJoin([
          of(e),
          this._projectService.getAgencyNetwork(),
          this._projectService.hasOnlyNetQuotes()
        ]))
    ).subscribe(e => {
      const params = this.createDetailParams(e[2], e[1].id_network);
      //const hasFlight = parseInt(e[0].get('hasFlight'), 10) === 1;
      const loadingItem: LoadingItem = {id: 'holiday-detail', message: 'Stiamo preparando l\'offerta per te...'};
      this._loadingService.addLoad(loadingItem);
      this.cruise$ = this._cruiseDetailService.getFormattedCruiseDetail(params, this.isLanding).pipe(
          map((x) => {
            if (x) {
              x.solutions = x.solutions.filter(s => s.hasFlight === x.solutions[0].hasFlight);

              x.steps = this.removeCoordinateFromHarbourName(x.steps);
              // filtro le solutions con la data di partenza uguale a quella impostata in rotta
              // NB: riformatto le date in un formato piu "sicuro" e addatto per poter applicare il filtro senza
              // eventuali problemi di fuso orario
              const filteredSolutionsByDepartureDate = x.solutions.filter(sol =>
                  dayjs(sol.departureDate).format('DDMMYYYY') === dayjs(this.routeParams.fromDate, 'DD-MM-YYYY').format('DDMMYYYY')
              );

              // dall'array "filteredSolutionsByDepartureDate" recupero la solution con il prezzo piu basso
              // NB: lascio la logica che c'era prima nel caso capiti che vengano ritornate piu di una solution
              // con la stessa data ma prezzo diverso
              x.selectedSolution = minBy(filteredSolutionsByDepartureDate, 'price');
            }
            const bookingObject = this._bookingObject.bookingObject;
            this._bookingObject.bookingObject = {
              ...bookingObject,
              cruise: x,
              selectedSolution: x.selectedSolution,
            };
            return x;
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
          catchError(err => throwError(err)),
          finalize(() => this._loadingService.removeLoad(loadingItem))
      );

      this.cruise$.pipe(takeUntil(this.unsubscribe$)).subscribe(cruise => {
        if (cruise) {
          this._seoService.setTitle(cruise.name);
          this._seoService.setImage(cruise.ship.image);
          this._seoService.setDescription(`Crociera nel ${cruise.parentName} a bordo di ${cruise.ship.name}`);
          this.shipService$ = this._cruiseDetailService.getShipService({ tourOperatorId: cruise.tourOperator.id, shipCode: cruise.ship.code });

          // se è stata selezionata una solution dal listato di ricerca, effettuo la chiamata category per aggiornare il
          // prezzo e la disponibilità
          // NB: SOLO sui siti GEO dato che, su quelli WTG, la category viene già effettuata nel componente della
          // griglia selezione solutions e tariffe/cabine
          if (![CruiseTopId.MSC, CruiseTopId.CARNIVAL].includes(cruise.tourOperator.id) && cruise.selectedSolution && this.brand.networkId === Networks.GEO) {
            // svuoto la cache delle chiamate "/category" per solution (SOLO su siti GEO)
            this._cruiseBookingService.updatedSolutionFromCruiseCategoryCache = undefined;

            // aggiorno prezzi cabine della solution selezionata e disponibilità
            this._cruiseBookingService.isLoadingUpdateSolutionFromCruiseCat$.next(true);
            this._cruiseBookingService.getUpdatedSolutionFromCruiseCategory(cruise, cruise.selectedSolution, this.occupancy.ages)
                .catch((error) => console.info(error))
                .finally(() => this._cruiseBookingService.isLoadingUpdateSolutionFromCruiseCat$.next(false));
          }
        } else {
          this.noDetail = true;
          console.error("La chiamata cruise ha restituito un errore");
        }
      });

      this._cruiseBookingService.selectedSolution$
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((selectedSolution) => {
          if (selectedSolution) {
            this.cruise$ = this._cruiseDetailService.getFormattedCruiseDetail(params).pipe(
                map(cruiseDetail => {
                  if (cruiseDetail) {
                    cruiseDetail.selectedSolution = selectedSolution;
                  }
                  cruiseDetail.steps = this.removeCoordinateFromHarbourName(cruiseDetail.steps);
                  return cruiseDetail;
                }),
                shareReplay({ bufferSize: 1, refCount: true }),
                catchError(err => throwError(err))
            );
          }
        });
    });
  }

  goToHomeRoute(): void {
    this._navigationService.getRedirectUrl('/').then(
        (resolved) => {
          if (resolved.isWidget) {
            // se sono in un widget, eseguo il redirect dall'esterno tramite postMessage
            // this._postMessageService.redirect(resolved.url);
            this._router.navigate([resolved.url]);
          } else {
            // ... altrimenti, effettuo direttamente il redirect tramite window.location.href
            window.location.href = resolved.url;
          }
        }
    );
  }

  createDetailParams(onlyNetworkQuotes?: boolean, network = 'null'): ICruiseDetailParams {
    let fromDate: number;
    let toDate: number;

    if (network && Number(network) === Networks.WTG) {
      // se un sito di wtg, allora effettuo una ricerca del dettaglio per 4 mesi partendo dal mese precedente a quello della
      // data di partenza inserita nell'url
      fromDate = dayjs(this.routeParams.fromDate, 'DD-MM-YYYY')
          .subtract(1, 'month')
          .startOf('month')
          .unix();
      toDate = dayjs(this.routeParams.fromDate, 'DD-MM-YYYY')
          .add(2, 'month')
          .endOf('month')
          .unix();
    } else {
      // se non si tratta di un sito wtg, allora effettuo una ricerca del dettaglio per la data di partenza inserita nell'url
      fromDate = dayjs(this.routeParams.fromDate, 'DD-MM-YYYY').unix();
      toDate = dayjs(this.routeParams.fromDate, 'DD-MM-YYYY').unix();
    }

    return {
      source: this.routeParams.source,
      from: fromDate,
      to: toDate,
      onlyNetworkQuotes,
      network
    };
  }

  private removeCoordinateFromHarbourName(steps: ICruiseDetailStep[]): ICruiseDetailStep[] {
    if(!steps.length) return [];

    return steps.map(step => ({
              ...step,
              harbours: step.harbours.map(harbour => ({
                ...harbour,
                data: {
                  ...harbour.data,
                  Name: harbour.data.Name.replace(/^.*?[NS],\s.*?[EW]\s/, '')
                }
              }))
            }));
  }

}
